import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from 'design-system/src/components/Button/Button';
import { AuthContext } from '../auth';
import PageContainer from '../components/PageContainer';
import RxTable from '../components/rx/RxTable';
import ProcessingRxTable from '../components/rx/ProcessingRxTable';
import { useFetchPrescriptions } from '../components/rx/useFetchPrescriptions';
import { useFetchProcessingPrescriptions } from '../components/rx/useFetchProcessingPrescriptions';
import { useSearchRx } from '../components/rx/useSearchRx';
import { RxPreviewItem } from '../components/rx/RxPreviewItem';
import type { SortingState } from '@tanstack/react-table';
import { getRxSortParams, getRxSortingState } from '../components/rx/utils';
import { useFetchPortalTableFilters } from '../components/useFetchPortalTableFilters';
import { useHasAwaitingOffice } from '../components/rx/useHasAwaitingOffice';
import { useHasAnyPrescriptions } from '../components/rx/useHasAnyPrescriptions';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { useSearchParams, Navigate, useNavigate } from 'react-router-dom';
import { RxFilterValues, RxStatus } from '../types';
import { downloadPrescriptionsTable } from '../components/rx/downloadPrescriptionsTable';
import { Toast } from 'design-system/src/components/Toast/Toast';
import toast from 'react-hot-toast';
import { DrugCategory, OverallStatusFilter } from '../api';
import { TableTabList, TabsAndSearchRow } from '../components/Table';
import { TableSearchWithPreview } from '../components/TableSearchWithPreview/TableSearchWithPreview';
import NotificationDot from 'design-system/src/components/NotificationDot/NotificationDot';
import { MIN_SEARCH_LENGTH, MULTI_SELECT_DELINEATOR } from '../constants';
import { NewEnrollmentModal } from '../components/enrollments/NewEnrollmentModal/NewEnrollmentModal';
import { PendingEnrollmentForms } from '../components/rx/PendingEnrollmentForms/PendingEnrollmentForms';
import { getInitialTableColumnFilterValueFromCookie, updateUrlSearchParams } from '../utils';
import { createTrackingProps } from 'shared-utils/analytics';

const RESULTS_PER_PAGE = 25;
const PROCESSING_RESULTS_PER_PAGE = 2 * RESULTS_PER_PAGE;
const RX_TABLE_MOBILE_BREAKPOINT = 1260;
const RX_TABLE_MOBILE_BREAKPOINT_WITH_PENDING = 1350;

const TabsContainer = styled(TabsAndSearchRow)`
  padding: 12px 0 0 0;
`;

const filterValuesFromSearchParams = (searchParams: URLSearchParams) => {
  return {
    prescriber: searchParams.get('prescriber') || '',
    office: searchParams.get('office') || '',
    drug: searchParams.get('drug_name') || '',
    drug_category: searchParams.get('drug_category') || '',
    status: searchParams.get('status') || '',
    overallStatus: searchParams.get('overall_status') || OverallStatusFilter.InProgress,
    awaitingOffice: searchParams.get('awaiting_office') === 'true' || false,
    awaitingPatient: searchParams.get('awaiting_patient') === 'true' || false,
    startDate: searchParams.get('created_at_start') || '',
    endDate: searchParams.get('created_at_end') || '',
  } as RxFilterValues;
};

const RxTablePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialPageIndex = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;
  const [pageIndex, setPageIndex] = useState(initialPageIndex);
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
  const [sorting, setSorting] = useState<SortingState>(
    getRxSortingState(searchParams.get('sort_by'), searchParams.get('sort_desc')),
  );
  const [newEnrollmentModalOpen, setNewEnrollmentModalOpen] = useState(false);
  const rxStatusPanelId = React.useId();
  const rxStatusTabId = React.useId();

  // Applied search is what's actually filtering the table (only updated on Enter)
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get('search') || '');

  // Fetch search results from the server for the search preview dropdown
  // Note: searchValue is already debounced by TableSearchWithPreview (300ms)
  const { data: searchResults, isFetching: isSearching } = useSearchRx(searchValue);

  const authContext = React.useContext(AuthContext);

  const handleSortingChange = (newSorting: SortingState) => {
    setSorting(newSorting);
    const { sort_by, sort_desc } = getRxSortParams(newSorting);
    const newParams = new URLSearchParams(searchParams);
    if (sort_by) {
      newParams.set('sort_by', sort_by);
    } else {
      newParams.delete('sort_by');
    }
    newParams.set('sort_desc', sort_desc ? 'true' : 'false');
    if (pageIndex !== 0) {
      setPageIndex(0);
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  useEffect(() => {
    const officeCookiesValues = getInitialTableColumnFilterValueFromCookie(
      authContext.user?.member_id || null,
      'office_name',
    );
    const officeSearchParam = searchParams.get('office');

    const drugCookieValues = getInitialTableColumnFilterValueFromCookie(
      authContext.user?.member_id || null,
      'drug_name',
    );
    const drugSearchParam = searchParams.get('drug_name');

    const drugCategoryCookieValues = getInitialTableColumnFilterValueFromCookie(
      authContext.user?.member_id || null,
      'drug_category',
    );
    const drugCategorySearchParam = searchParams.get('drug_category');

    const defaultPrescriberValues = authContext.user?.default_filters?.providers.map((p) => p.npi) || [];
    const prescriberSearchParam = searchParams.get('prescriber');

    // we can't use setOfficeFilter or setPrescriberFilter because if they're both set,
    // the second method call will overwrite the first one
    if (
      (officeCookiesValues.length && !officeSearchParam) ||
      (drugCookieValues.length && !drugSearchParam) ||
      (drugCategoryCookieValues.length && !drugCategorySearchParam) ||
      (defaultPrescriberValues.length && !prescriberSearchParam)
    ) {
      const newUrlSearchParams: Record<string, string | undefined> = {};
      const newServerSideFilterValues: RxFilterValues = { ...filterValues };

      if (officeCookiesValues.length && !officeSearchParam) {
        const officeValuesString = officeCookiesValues.join(MULTI_SELECT_DELINEATOR);
        newUrlSearchParams.office = officeValuesString;
        newServerSideFilterValues.office = officeValuesString;
      }

      if (drugCookieValues.length && !drugSearchParam) {
        const drugValuesString = drugCookieValues.join(MULTI_SELECT_DELINEATOR);
        newUrlSearchParams.drug_name = drugValuesString;
        newServerSideFilterValues.drug = drugValuesString;
      }

      if (drugCategoryCookieValues.length && !drugCategorySearchParam) {
        const drugCategoryValuesString = drugCategoryCookieValues.join(MULTI_SELECT_DELINEATOR);
        newUrlSearchParams.drug_category = drugCategoryValuesString;
        newServerSideFilterValues.drug_category = drugCategoryValuesString;
      }

      if (defaultPrescriberValues.length && !prescriberSearchParam) {
        const prescriberValuesString = defaultPrescriberValues.join(MULTI_SELECT_DELINEATOR);
        newUrlSearchParams.prescriber = prescriberValuesString;
        newServerSideFilterValues.prescriber = prescriberValuesString;
      }
      updateFilterOrSearchValues(newUrlSearchParams);
      setFilterValues(newServerSideFilterValues);
    }
    // TODO(WIT-4362): fix
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // used to fetch serverside filtered data
  const [filterValues, setFilterValues] = useState<RxFilterValues>(filterValuesFromSearchParams(searchParams));
  const [total, setTotal] = useState(0);

  const isProcessingTabActive = searchParams.get('processing') === 'true';

  // When search is applied, we show search results instead of normal table data
  const isSearchActive = !!appliedSearch;

  const { data: hasAwaitingOffice } = useHasAwaitingOffice(appliedSearch, filterValues);
  const { data: hasAnyPrescriptions } = useHasAnyPrescriptions();

  const prescriptionsQuery = useFetchPrescriptions(
    pageIndex,
    RESULTS_PER_PAGE,
    appliedSearch,
    filterValues,
    sorting,
    !isProcessingTabActive, // disabled when in processing tab
  );

  const [processingPageIndex, setProcessingPageIndex] = useState(0);

  const faxIntakesQuery = useFetchProcessingPrescriptions(processingPageIndex, PROCESSING_RESULTS_PER_PAGE); // always enabled to determine presence of processing tab

  // Only set hasProcessingPrescriptions on first successful load
  const hasProcessingPrescriptions = !!faxIntakesQuery.data?.total && faxIntakesQuery.data.total > 0;

  const data = isProcessingTabActive ? faxIntakesQuery.data : prescriptionsQuery.data;
  const isLoading = isProcessingTabActive ? faxIntakesQuery.isLoading : prescriptionsQuery.isLoading;
  const error = isProcessingTabActive ? faxIntakesQuery.error : prescriptionsQuery.error;

  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDownload = async () => {
    try {
      toast.custom(() => <Toast variant="default">Download started! Please keep this window open.</Toast>);
      setDownloadLoading(true);
      const { sort_by, sort_desc } = getRxSortParams(sorting);
      const downloadData = await downloadPrescriptionsTable(
        pageIndex,
        RESULTS_PER_PAGE,
        appliedSearch,
        filterValues,
        sort_by,
        sort_desc,
      );
      if (!downloadData) return;

      const blob = new Blob([downloadData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prescriptions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.custom(() => <Toast variant="success">Download complete.</Toast>);
    } catch (error) {
      toast.custom(() => <Toast variant="error">Download failed.</Toast>);
    } finally {
      setDownloadLoading(false);
    }
  };

  const {
    data: filterOptionsData,
    isLoading: filterOptionsLoading,
    error: filterOptionsError,
  } = useFetchPortalTableFilters();

  useEffect(() => {
    if (data) {
      setTotal(data.total);
    }
  }, [data]);

  const setPrescriberFilter = (prescriberNpisList: string[]) => {
    const prescriberNpisString = prescriberNpisList.join(MULTI_SELECT_DELINEATOR);
    setFilterValues({ ...filterValues, prescriber: prescriberNpisString });
    updateFilterOrSearchValues({
      prescriber: prescriberNpisString,
    });
  };

  const setOfficeFilter = (officeNamesList: string[]) => {
    const officeNamesString = officeNamesList.join(MULTI_SELECT_DELINEATOR);
    setFilterValues({ ...filterValues, office: officeNamesString });
    updateFilterOrSearchValues({
      office: officeNamesString,
    });
  };

  const setDrugNameFilter = (drugNamesList: string[]) => {
    const drugNamesString = drugNamesList.join(MULTI_SELECT_DELINEATOR);
    setFilterValues({ ...filterValues, drug: drugNamesString });
    updateFilterOrSearchValues({ drug_name: drugNamesString });
  };

  const setDrugCategoryFilter = (drugCategoriesList: DrugCategory[]) => {
    const drugCategoriesString = drugCategoriesList.join(MULTI_SELECT_DELINEATOR);
    setFilterValues({ ...filterValues, drug_category: drugCategoriesString });
    updateFilterOrSearchValues({ drug_category: drugCategoriesString });
  };

  const setStatusFilter = (statusList: RxStatus[]) => {
    const statusString = statusList.join(MULTI_SELECT_DELINEATOR);
    setFilterValues({ ...filterValues, status: statusString });
    updateFilterOrSearchValues({ status: statusString });
  };

  const setDateRangeFilter = ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
    const normalizedStartDate = startDate ?? '';
    const normalizedEndDate = endDate ?? '';
    setFilterValues({
      ...filterValues,
      startDate: normalizedStartDate,
      endDate: normalizedEndDate,
    });
    updateFilterOrSearchValues({
      created_at_start: startDate || undefined,
      created_at_end: endDate || undefined,
    });
  };

  type StatusTabValue = 'open' | 'awaiting_office' | 'awaiting_patient' | 'completed' | 'all' | 'processing';

  const setStatusTab = (tab: StatusTabValue) => {
    let overallStatus = OverallStatusFilter.InProgress;
    let awaitingOffice = false;
    let awaitingPatient = false;
    let isProcessingTabSelected = false;

    if (tab === 'completed') {
      overallStatus = OverallStatusFilter.Completed;
    } else if (tab === 'processing') {
      isProcessingTabSelected = true;
    } else if (tab === 'awaiting_office') {
      awaitingOffice = true;
    } else if (tab === 'awaiting_patient') {
      awaitingPatient = true;
    } else if (tab === 'all') {
      overallStatus = OverallStatusFilter.All;
    }

    setFilterValues({
      ...filterValues,
      overallStatus,
      awaitingOffice,
      awaitingPatient,
    });

    updateFilterOrSearchValues({
      overall_status: isProcessingTabSelected ? undefined : overallStatus, // Clear overallStatus in processing tab
      awaiting_office: awaitingOffice ? 'true' : undefined,
      awaiting_patient: awaitingPatient ? 'true' : undefined,
      processing: isProcessingTabSelected ? 'true' : undefined,
    });
  };

  // updates the url
  const updateFilterOrSearchValues = (keyValues: Record<string, string | undefined>) => {
    updateUrlSearchParams(searchParams, setSearchParams, keyValues, true);
    setPageIndex(0);
  };

  const goBack = () => {
    const newPageIndex = pageIndex - 1;
    setPageIndex(newPageIndex);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPageIndex + 1));
    setSearchParams(newParams);
  };

  const goForward = () => {
    const newPageIndex = pageIndex + 1;
    setPageIndex(newPageIndex);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPageIndex + 1));
    setSearchParams(newParams);
  };

  const goBackProcessing = () => {
    setProcessingPageIndex(processingPageIndex - 1);
  };

  const goForwardProcessing = () => {
    setProcessingPageIndex(processingPageIndex + 1);
  };

  const submitSearchValue = (value: string) => {
    setSearchValue(value);
    updateFilterOrSearchValues({ search: value || undefined });
  };

  const clearSearch = React.useCallback(() => {
    setSearchValue('');
    setAppliedSearch('');
    updateUrlSearchParams(searchParams, setSearchParams, { search: undefined });
  }, [searchParams, setSearchParams]);

  if (!authContext.user) {
    return <LoadingSpinner />;
  }

  if (!authContext.user.tabs.includes('prescriptions')) {
    // The / route shows the prescriptions page, but some practices have this tab disabled
    // and need to be redirected to the analytics, tasks, or onboarding page
    if (authContext.user.tabs.includes('onboarding')) {
      return <Navigate to="/onboarding" replace />;
    } else if (authContext.user.tabs.includes('tasks')) {
      return <Navigate to="/tasks" replace />;
    } else {
      return <Navigate to="/analytics" replace />;
    }
  }

  const getSelectedTabValue = (): StatusTabValue | '' => {
    // Deselect all tabs when search is active
    if (isSearchActive) return '';
    if (isProcessingTabActive) return 'processing';
    if (filterValues.overallStatus === OverallStatusFilter.Completed) return 'completed';
    if (filterValues.awaitingOffice) return 'awaiting_office';
    if (filterValues.awaitingPatient) return 'awaiting_patient';
    if (filterValues.overallStatus === OverallStatusFilter.All) return 'all';
    return 'open';
  };

  let body;
  if (filterOptionsLoading) {
    body = <LoadingSpinner />;
  } else if (error || filterOptionsError || !filterOptionsData) {
    body = <PageLoadError />;
  } else {
    body = (
      <>
        <TabsContainer
          $mobileBreakpoint={
            hasProcessingPrescriptions ? RX_TABLE_MOBILE_BREAKPOINT_WITH_PENDING : RX_TABLE_MOBILE_BREAKPOINT
          }
        >
          <TableTabList
            tabs={[
              { label: 'All open', value: 'open' },
              {
                label: hasAwaitingOffice ? (
                  <NotificationDot srOnlyText="there are prescriptions awaiting office review">
                    Awaiting office
                  </NotificationDot>
                ) : (
                  'Awaiting office'
                ),
                value: 'awaiting_office',
              },
              { label: 'Awaiting patient', value: 'awaiting_patient' },
              { label: 'Completed', value: 'completed' },
              { label: 'All prescriptions', value: 'all' },
              ...(hasProcessingPrescriptions ? [{ label: 'Pending', value: 'processing' as const }] : []),
            ]}
            selectedValue={getSelectedTabValue()}
            onSelect={(value) => {
              // Clear search when switching tabs
              if (appliedSearch) {
                setSearchValue('');
                setAppliedSearch('');
                updateUrlSearchParams(searchParams, setSearchParams, {
                  search: undefined,
                });
              }
              setStatusTab(value as StatusTabValue);
            }}
            tabListId={rxStatusTabId}
            tabPanelId={rxStatusPanelId}
            ariaLabel="Filter prescriptions by status"
            {...createTrackingProps({ elementName: 'Prescriptions Table: Status Tabs', unmask: true })}
          />
          <TableSearchWithPreview
            tableName="Prescriptions Table"
            value={searchValue}
            onChange={setSearchValue}
            onApplySearch={(value) => {
              setAppliedSearch(value);
              // When searching, show results from all tabs
              if (value) {
                setFilterValues({
                  ...filterValues,
                  overallStatus: OverallStatusFilter.All,
                  awaitingOffice: false,
                  awaitingPatient: false,
                });
                updateUrlSearchParams(searchParams, setSearchParams, {
                  search: value,
                  overall_status: OverallStatusFilter.All,
                  awaiting_office: undefined,
                  awaiting_patient: undefined,
                  processing: undefined,
                });
              } else {
                updateUrlSearchParams(searchParams, setSearchParams, {
                  search: undefined,
                });
              }
            }}
            onClear={() => {
              setAppliedSearch('');
              updateUrlSearchParams(searchParams, setSearchParams, {
                search: undefined,
              });
            }}
            data={searchResults?.results || []}
            getItemKey={(rx) => rx.id}
            onPreviewItemClick={(rx) => {
              navigate(`/rxs/${rx.id}`);
            }}
            renderPreviewItem={(rx) => <RxPreviewItem rx={rx} searchTerm={searchValue} />}
            isLoading={isSearching}
            minSearchLength={MIN_SEARCH_LENGTH}
            mobileBreakpoint={
              hasProcessingPrescriptions ? RX_TABLE_MOBILE_BREAKPOINT_WITH_PENDING : RX_TABLE_MOBILE_BREAKPOINT
            }
          />
        </TabsContainer>

        {isProcessingTabActive ? (
          <ProcessingRxTable
            data={data?.results || []}
            loading={isLoading}
            tableProps={{
              total: data?.total || 0,
              pageIndex: processingPageIndex,
              perPage: PROCESSING_RESULTS_PER_PAGE,
              goBack: goBackProcessing,
              goForward: goForwardProcessing,
              submitSearchValue,
              filters: filterOptionsData,
              setPrescriberFilter,
              setDrugNameFilter,
            }}
            initialSearchValue={appliedSearch}
            initialFilterValues={filterValues}
            tabPanelId={rxStatusPanelId}
            tabId={rxStatusTabId}
          />
        ) : (
          <RxTable
            data={data?.results || []}
            loading={isLoading}
            tableProps={{
              total,
              pageIndex,
              perPage: RESULTS_PER_PAGE,
              submitSearchValue,
              goBack,
              goForward,
              filters: filterOptionsData,
              setPrescriberFilter,
              setOfficeFilter,
              setDrugNameFilter,
              setDrugCategoryFilter,
              setStatusFilter,
              setDateRangeFilter,
              sorting,
              setSorting: handleSortingChange,
            }}
            initialSearchValue={appliedSearch}
            initialFilterValues={filterValues}
            handleDownload={handleDownload}
            downloadLoading={downloadLoading}
            tabPanelId={rxStatusPanelId}
            tabId={rxStatusTabId}
            onClearSearch={clearSearch}
            hasProcessingPrescriptions={hasProcessingPrescriptions}
            hasAnyPrescriptions={hasAnyPrescriptions}
          />
        )}
      </>
    );
  }

  const headerRight = (
    <div className="flex items-center gap-3">
      <PendingEnrollmentForms />
      <Button
        size="sm"
        onClick={() => setNewEnrollmentModalOpen(true)}
        {...createTrackingProps({ elementName: 'Rx Table Page: New In-Office Enrollment Button' })}
      >
        + Create enrollment form
      </Button>
    </div>
  );

  return (
    <PageContainer title="Prescriptions" headerRight={headerRight}>
      {body}

      {newEnrollmentModalOpen && <NewEnrollmentModal onClose={() => setNewEnrollmentModalOpen(false)} />}
    </PageContainer>
  );
};

export default RxTablePage;
